import { getEmbeddedClient, isEmbeddedInMobileApp } from '@/services/Security/EmbeddedClient'

const FINGERPRINT_VERSION = 'v1'
const COLLECTION_TIMEOUT_MS = 1500
const CANVAS_SIZE = { width: 240, height: 60 }
const FONT_PROBE_TEXT = 'mmmmmmmmmmlli'
const FONT_PROBE_SIZE = '72px'
const BASE_FONTS = ['monospace', 'sans-serif', 'serif']
// Fonts that tell systems and installed software apart (Windows, macOS, Linux, Office, Adobe...).
const PROBED_FONTS = [
	'Arial', 'Arial Narrow', 'Calibri', 'Cambria', 'Candara', 'Comic Sans MS', 'Consolas', 'Courier New', 'Franklin Gothic Medium',
	'Georgia', 'Helvetica', 'Helvetica Neue', 'Impact', 'Lucida Console', 'Lucida Grande', 'Menlo', 'Monaco', 'Palatino Linotype',
	'Segoe UI', 'Segoe UI Emoji', 'Tahoma', 'Times New Roman', 'Trebuchet MS', 'Verdana', 'Ubuntu', 'DejaVu Sans', 'Liberation Sans',
	'Noto Sans', 'Roboto', 'San Francisco', 'Apple Color Emoji', 'Gill Sans', 'Optima', 'Futura', 'Avenir', 'Rockwell', 'Garamond',
	'Book Antiqua', 'Century Gothic', 'MS Gothic', 'SimSun', 'Microsoft YaHei', 'Wingdings', 'Myriad Pro', 'Minion Pro'
]

const sha256Hex = async text => {
	const bytes = new TextEncoder().encode(text)
	if (window.crypto?.subtle) {
		const digest = await window.crypto.subtle.digest('SHA-256', bytes)
		return Array.from(new Uint8Array(digest), byte => byte.toString(16).padStart(2, '0')).join('')
	}
	// Insecure contexts (plain http, development) have no SubtleCrypto: a 256 bits mix of the text, not a cryptographic hash.
	const words = new Array(8).fill(0).map((_, index) => 0x9e3779b9 * (index + 1))
	bytes.forEach((byte, position) => {
		const index = position % 8
		words[index] = Math.imul(words[index] ^ byte, 0x01000193) >>> 0
		words[(index + 1) % 8] = (words[(index + 1) % 8] + words[index]) >>> 0
	})
	return words.map(word => word.toString(16).padStart(8, '0')).join('')
}

const safely = (collector, fallback = null) => {
	try {
		return collector()
	} catch (error) {
		return fallback
	}
}

const canvasTrait = documentRef => {
	const canvas = documentRef.createElement('canvas')
	canvas.width = CANVAS_SIZE.width
	canvas.height = CANVAS_SIZE.height
	const context = canvas.getContext('2d')
	if (!context) {
		return null
	}
	context.textBaseline = 'alphabetic'
	context.fillStyle = '#f60'
	context.fillRect(100, 1, 62, 20)
	context.fillStyle = '#069'
	context.font = '14px "Arial"'
	context.fillText('Welyb, <canvas> 1.0 \u{1F600}', 2, 15)
	context.fillStyle = 'rgba(102, 204, 0, 0.7)'
	context.font = '18px serif'
	context.fillText('Welyb, <canvas> 1.0 \u{1F600}', 4, 45)
	context.globalCompositeOperation = 'multiply'
	context.fillStyle = 'rgb(255,0,255)'
	context.beginPath()
	context.arc(50, 30, 20, 0, Math.PI * 2, true)
	context.fill()
	return canvas.toDataURL()
}

const webglTrait = documentRef => {
	const canvas = documentRef.createElement('canvas')
	const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl')
	if (!gl) {
		return null
	}
	const debugInfo = gl.getExtension('WEBGL_debug_renderer_info')
	return [
		debugInfo ? gl.getParameter(debugInfo.UNMASKED_VENDOR_WEBGL) : gl.getParameter(gl.VENDOR),
		debugInfo ? gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL) : gl.getParameter(gl.RENDERER),
		gl.getParameter(gl.MAX_TEXTURE_SIZE),
		gl.getParameter(gl.MAX_RENDERBUFFER_SIZE),
		gl.getParameter(gl.MAX_VERTEX_ATTRIBS),
		(gl.getSupportedExtensions() ?? []).length
	].join('|')
}

const installedFontsTrait = documentRef => {
	const container = documentRef.createElement('span')
	container.style.cssText = `position:absolute;left:-9999px;font-size:${FONT_PROBE_SIZE};visibility:hidden`
	container.textContent = FONT_PROBE_TEXT
	documentRef.body.appendChild(container)
	try {
		const measure = fontFamily => {
			container.style.fontFamily = fontFamily
			return `${container.offsetWidth}x${container.offsetHeight}`
		}
		const baseSizes = BASE_FONTS.map(measure)
		return PROBED_FONTS.map(font => (BASE_FONTS.some((base, index) => measure(`'${font}',${base}`) !== baseSizes[index]) ? '1' : '0')).join('')
	} finally {
		documentRef.body.removeChild(container)
	}
}

/**
 * Traits of the device the webapp runs on that do not change from one session to the next, nor with a browser update
 * (no version, no window size, no zoom, no preference the user changes by hand). Reduced to the biggest side of the
 * screen so that rotating a tablet is not a new device.
 */
export const collectTraits = (env = { window, navigator, screen, document }) => {
	const { window: windowRef, navigator: navigatorRef, screen: screenRef, document: documentRef } = env
	const matches = query => safely(() => windowRef.matchMedia(query).matches)

	return {
		screen: safely(() => [Math.max(screenRef.width, screenRef.height), Math.min(screenRef.width, screenRef.height), screenRef.colorDepth].join('x')),
		timezone: safely(() => Intl.DateTimeFormat().resolvedOptions().timeZone),
		languages: safely(() => (navigatorRef.languages?.length ? navigatorRef.languages.join(',') : navigatorRef.language)),
		platform: safely(() => navigatorRef.platform),
		cores: safely(() => navigatorRef.hardwareConcurrency),
		memory: safely(() => navigatorRef.deviceMemory),
		touchPoints: safely(() => navigatorRef.maxTouchPoints),
		vendor: safely(() => navigatorRef.vendor),
		colorGamut: ['rec2020', 'p3', 'srgb'].find(gamut => matches(`(color-gamut: ${gamut})`)) ?? null,
		hdr: matches('(dynamic-range: high)'),
		pointer: ['fine', 'coarse', 'none'].find(pointer => matches(`(pointer: ${pointer})`)) ?? null,
		hover: matches('(hover: hover)'),
		canvas: safely(() => canvasTrait(documentRef)),
		webgl: safely(() => webglTrait(documentRef)),
		fonts: safely(() => installedFontsTrait(documentRef))
	}
}

/**
 * Hash (64 hex characters) of the traits: it identifies the device, it says nothing readable about it.
 */
export const hashTraits = traits => sha256Hex(`${FINGERPRINT_VERSION}|${JSON.stringify(traits)}`)

let fingerprintPromise = null

/**
 * Fingerprint of this device, computed once. Null (never an error) when it cannot be computed or when the webapp runs
 * in a webview of the mobile app, whose account belongs to the person holding the phone.
 */
export const getDeviceFingerprint = () => {
	if (!fingerprintPromise) {
		fingerprintPromise = isEmbeddedInMobileApp(getEmbeddedClient())
			? Promise.resolve(null)
			: Promise.resolve()
					.then(() => hashTraits(collectTraits()))
					.catch(() => null)
	}
	return fingerprintPromise
}

/**
 * Same, without ever delaying a request for more than a moment.
 */
export const getDeviceFingerprintWithin = (timeoutMs = COLLECTION_TIMEOUT_MS) =>
	Promise.race([getDeviceFingerprint(), new Promise(resolve => setTimeout(() => resolve(null), timeoutMs))])

export const resetDeviceFingerprint = () => {
	fingerprintPromise = null
}
